import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { buttonVariants, HeroImage } from '@/shared/ui'
import { MAGNIFIC_URL, pickRandomHeroImage } from '@/shared/config'
import { cn } from '@/shared/lib'

const SRD_URL = 'https://www.dndbeyond.com/srd'

type Item = { id: string; title: React.ReactNode; text: string }

const DOES: Item[] = [
  {
    id: 'builds',
    title: 'Builds characters step by step',
    text: 'Species, class, background, ability scores, skills, spells, feats and equipment, one decision at a time, with the rules for each choice built in.',
  },
  {
    id: 'math',
    title: 'Does the math for you',
    text: 'Armor Class, Hit Points, saving throws, skill bonuses and spell save DC are worked out from your choices and stay correct as they change, and the sheet explains how each one is made up.',
  },
  {
    id: 'table',
    title: 'Plays at the table',
    text: 'Roll attacks, checks and saves with 3D dice, and track Hit Points, spell slots, class resources, rests, inventory and coins.',
  },
  {
    id: 'levels',
    title: 'Levels up from 1 to 20',
    text: 'Each level lists what you gain and asks only for the choices you actually have to make.',
  },
  {
    id: 'multiclass',
    title: 'Multiclasses (alpha)',
    text: 'A character can take levels in more than one class. This feature is in alpha: it follows the SRD rules, but expect rough edges, and tell us when something looks wrong.',
  },
  {
    id: 'keeps',
    title: 'Keeps your characters',
    text: 'Characters are saved in your browser without an account. Sign in to sync them across devices or share a read-only link, and download or print them any time.',
  },
  {
    id: 'group',
    title: 'Brings your group together',
    text: 'Parties let a Dungeon Master invite players and share the campaign’s homebrew with everyone at once.',
  },
  {
    id: 'homebrew',
    title: 'Takes your homebrew',
    text: 'Add your own species, classes, subclasses, spells, items and feats as content packs, and they work like the built-in rules.',
  },
]

const DOES_NOT: Item[] = [
  {
    id: 'books',
    title: (
      <>
        Include any official book, except the SRD
        <a href="#srd-note" className="text-link underline underline-offset-2">
          <span aria-hidden="true">*</span>
          <span className="sr-only"> (see the note on the SRD)</span>
        </a>
      </>
    ),
    text: 'Material printed only in other books, like most subclasses and many species, isn’t included. You can add your own through content packs.',
  },
  {
    id: 'game',
    title: 'Run the game',
    text: 'It’s a character sheet, not a virtual tabletop. There are no maps, monsters, encounters or initiative tracking.',
  },
  {
    id: 'host',
    title: 'Host your content',
    text: 'Content packs stay in your browser or at a link you control. The app never publishes them for you.',
  },
  {
    id: 'overrule',
    title: 'Overrule your table',
    text: 'Where the rules leave timing to the table, like when you change prepared spells, the app lets you do it and leaves the call to your group.',
  },
  {
    id: 'wotc',
    title: 'Speak for Wizards of the Coast',
    text: 'It’s an independent fan project, not affiliated with or endorsed by Wizards of the Coast.',
  },
]

function List({ items, icon: Icon, iconClass }: {
  items: Item[]
  icon: typeof Check
  iconClass: string
}) {
  return (
    <ul className="flex flex-col gap-4">
      {items.map((item) => (
        <li key={item.id} className="flex gap-3">
          <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', iconClass)} />
          <div className="flex flex-col gap-0.5">
            <strong className="font-medium text-foreground">{item.title}</strong>
            <span>{item.text}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

export default function HomePage() {
  const [heroImage] = useState(() => pickRandomHeroImage())
  return (
    <div className="flex flex-col gap-10 text-sm leading-relaxed text-muted-foreground">
      <HeroImage
        src={heroImage.imageUrl}
        alt={heroImage.title}
        credit={{ name: 'Magnific', url: MAGNIFIC_URL, author: heroImage.author }}
        className="-mt-6"
      />
      <header className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Tome of Many Faces</h1>
        <p className="text-base">
          A free character builder and sheet for the 2024 fifth-edition rules. It runs in your browser and
          needs no account or install. With an account, your characters are saved to the cloud and sync
          effortlessly between your devices.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to="/faces" className={cn(buttonVariants({ size: 'lg' }), 'h-10 px-4')}>Your characters</Link>
          <Link to="/custom-content" className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'h-10 px-4')}>Add custom content</Link>
        </div>
      </header>

      <div className="grid gap-10 md:grid-cols-2">
        <section aria-labelledby="home-does" className="flex flex-col gap-4">
          <h2 id="home-does" className="text-lg font-semibold tracking-tight text-foreground">What it does</h2>
          <List items={DOES} icon={Check} iconClass="text-link" />
        </section>
        <section aria-labelledby="home-does-not" className="flex flex-col gap-4">
          <h2 id="home-does-not" className="text-lg font-semibold tracking-tight text-foreground">What it doesn’t do</h2>
          <List items={DOES_NOT} icon={X} iconClass="text-muted-foreground" />
          <p id="srd-note" className="scroll-mt-20 border-t pt-3 text-xs">
            * The built-in rules are the{' '}
            <a href={SRD_URL} target="_blank" rel="noreferrer" className="font-medium text-foreground underline underline-offset-2 hover:text-link">
              System Reference Document 5.2.1
            </a>{' '}
            (the 2024 rules), which Wizards of the Coast publishes for free under Creative Commons.
          </p>
        </section>
      </div>
    </div>
  )
}
